// The user-master / user-role layout for every administration screen:
//
//   page head (title, one-line subtitle, the page's own toolbar + buttons)
//   ┌ list panel ─────────────┐ ┌ detail panel ───────────────────┐
//   │ search  (F3)            │ │ header: avatar, name, New/Saved │
//   │ filters                 │ │ tabs                            │
//   │ segments (All / Active) │ │ the open pane                   │
//   │ select-all + sort bar   │ │                                 │
//   │ rows, 25 at a time      │ │ Delete · Reset · Save           │
//   │ Showing x of y          │ │                                 │
//   └─────────────────────────┘ └─────────────────────────────────┘
//   keyboard hints
//
// Layout only. Every handler, list, field and save comes from the page, so
// permissions, validation and the API calls behave exactly as before; the page
// hands its pieces over instead of laying them out itself.
import React from "react";
import { AxSearchIcon, AxClearIcon } from "./adminUi";

const AdminScreen = ({
  // page head
  title,
  subtitle,
  tools, // toolbar icon buttons (export / print / refresh …)
  actions, // primary buttons (Branch Transfer, New …)
  rail, // optional strip between the page head and the panels (alert chips)
  // list panel
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder = "Search…",
  searchHint = "F3",
  filters, // <AxFilterSelect> row
  segments, // segmented counters
  list, // <AdminList>
  // detail panel
  detail,
  split = true,
  tableView = false,
  // chrome
  status,
  children, // drawers, popups and anything that lives outside the grid
}) => {
  const hasSearch = typeof onSearchChange === "function";
  // a screen with no list (Company) is one panel holding the detail; a screen
  // with a list but no form (Bulk Edit, Product Import) is one panel holding it
  const onePanel = !list || !split;
  return (
    <div
      className={
        "axa-screen" + (onePanel ? " axa-single" : "") + (tableView ? " axa-tableview" : "")
      }
    >
      <div className="page-wrapper">
        <div className="content">
          {/* ============ PAGE HEAD ============ */}
          <div className="axa-phead">
            <div className="axa-ptitle">
              <h2>{title}</h2>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <div className="axa-ptools">
              {tools}
              {tools && actions ? <span className="axa-sepbar" /> : null}
              {actions}
            </div>
          </div>

          {rail ? <div className="axa-rail">{rail}</div> : null}

          <div className="axa-grid">
            {/* ============ LIST ============ */}
            {list ? (
              <section className="axa-panel axa-left">
                {hasSearch && (
                  <div className="axa-lhead">
                    <div className={"axa-searchwrap" + (searchHint ? "" : " nohint")}>
                      <AxSearchIcon />
                      <input
                        type="text"
                        className="axa-search"
                        autoComplete="off"
                        placeholder={searchPlaceholder}
                        value={searchValue ?? ""}
                        onChange={onSearchChange}
                      />
                      {searchValue && typeof onSearchClear === "function" ? (
                        <button
                          type="button"
                          className="axa-clr"
                          aria-label="Clear search"
                          onClick={onSearchClear}
                        >
                          <AxClearIcon />
                        </button>
                      ) : null}
                      {searchHint ? <span className="axa-kbd">{searchHint}</span> : null}
                    </div>
                  </div>
                )}

                {filters ? <div className="axa-filters">{filters}</div> : null}
                {segments ? <div className="axa-segs">{segments}</div> : null}

                {list}
              </section>
            ) : null}

            {/* ============ DETAIL ============ */}
            {(split || !list) && detail ? (
              <section className={"axa-panel " + (list ? "axa-right" : "axa-only")}>
                {detail}
              </section>
            ) : null}
          </div>

          {status ? <footer className="axa-status">{status}</footer> : null}
        </div>
      </div>
      {children}
    </div>
  );
};

export default AdminScreen;
